import styles from './UpsertProtocol.module.css';

import addIcon from 'assets/svg/add-01.svg';
import attentionIcon from 'assets/svg/attention-01.svg';

import classNames from 'classnames';
import DropdownComponent from 'components/Widgets/Dropdown/DropdownComponent';
import TextField from 'ui/TextField';
import Icon from 'helpers/components/Icon';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';
import { ProtocolOperations, protocolsStore } from 'store/mobx/protocols.store';
import { UpsertProtocolProvider, useUpsertProtocolContext } from './UpsertProtocolProvider';
import ProtocolOperation from './ProtocolOperation';
import SwitchWidget from '../../../Widgets/Switch';
import { useListDrawerContext } from '../../../../widgets/ListNavigationDrawer/ListDrawerProvider';
import useAlert from 'hooks/useAlert';

const UpsertProtocol = () => {
  return (
    <UpsertProtocolProvider>
      <UpsertProtocolComponent />
    </UpsertProtocolProvider>
  );
};

const UpsertProtocolComponent = observer(() => {
  const { showAlert } = useAlert();

  const { setDisabled } = useListDrawerContext();

  const {
    selectedProtocol,
    updateSelectedProtocol,
    upsertProtocol,
    protocolOperationMode,
    setProtocolOperationMode,
    getByIdProtocol,
    selectedProtocolId,
    removeProtocol,
    resetProtocol,
    toggleActivateProtocol,
  } = protocolsStore;

  const {
    name,
    isActive,
    selectedGroup,
    operations,
    isFormValidate,
    editMode,
    setName,
    setIsActive,
    setSelectedGroup,
    setOperations,
    setIsFormValidate,
    setEditMode,
    groupsOptions,
  } = useUpsertProtocolContext();

  useEffect(() => {
    setDisabled(editMode);
  }, [editMode]);

  useEffect(() => {
    return () => {
      resetProtocol();
    };
  }, []);

  useEffect(() => {
    if (selectedProtocolId && protocolOperationMode === ProtocolOperations.Add)
      return setProtocolOperationMode(ProtocolOperations.View);

    if (protocolOperationMode === ProtocolOperations.View) {
      setEditMode(false);
    }

    if (protocolOperationMode === ProtocolOperations.Add) {
      setIsActive(true);
      setEditMode(true);
      setName(selectedProtocol()?.name);
      setSelectedGroup(selectedProtocol()?.id_group);
      setOperations(selectedProtocol()?.operations ?? []);
      setIsFormValidate(false);
      return;
    }
    loadProtocol();
  }, [protocolOperationMode, selectedProtocolId]);

  useEffect(() => {
    updateSelectedProtocol({
      ...selectedProtocol(),
      is_active: isActive,
      name: name,
      id_group: selectedGroup,
      operations: operations,
    });
  }, [name, selectedGroup, operations, isActive]);

  const loadProtocol = async () => {
    if (selectedProtocolId) {
      const result = await getByIdProtocol(selectedProtocolId);
      updateSelectedProtocol(result);

      setName(result.name);
      setSelectedGroup(result.id_group);
      setOperations(result.operations ?? []);
      setIsActive(result.is_active);
      setIsFormValidate(false);
    }
  };

  const moveOperationListItem = (index1, index2) => {
    if (index1 < 0 || index1 >= operations.length || index2 < 0 || index2 >= operations.length)
      return;
    setOperations((prevOperations) => {
      const newOperations = [...prevOperations];
      [newOperations[index1], newOperations[index2]] = [
        newOperations[index2],
        newOperations[index1],
      ];
      return newOperations;
    });
  };

  const isProtocolValid = () => {
    return (
      name &&
      selectedGroup &&
      operations &&
      !operations.some((operation) => operation.text === '') &&
      !operations.some((operation) =>
        operation.devices.some((device) =>
          device.user_fields.some((field) => {
            if (field.required) {
              return field.value === undefined || field.value.value === '';
            }
          }),
        ),
      )
    );
  };

  const validateForm = () => {
    setIsFormValidate(true);
    const model = selectedProtocol();
    if (!isProtocolValid()) return false;
    if (model?.operations.length === 0) {
      alarmAddDeviceInProtocolAlert();
      return false;
    }
    if (model?.id) warningProtocolWillChangeAlert(submitForm);
    else submitForm();
  };

  const submitForm = async () => {
    const result = await upsertProtocol();

    if (!result.success && result.error_code === 2043) return alarmNameNotUniqueAlert();
    if (!result.success && result.error_code) return errorCommon(result.error_code, result.error);

    successProtocolUpsertAlert();
    setEditMode(false);
    setProtocolOperationMode(ProtocolOperations.View);
  };

  const handleSubmitClick = () => validateForm();

  const handleCancelClick = () => {
    setEditMode(false);
    setIsFormValidate(false);
    setProtocolOperationMode(ProtocolOperations.View);
    if (!selectedProtocolId) updateSelectedProtocol(null);
  };

  const handleEditClick = async () => {
    setEditMode(true);
    setProtocolOperationMode(ProtocolOperations.Edit);
  };

  const handleDeleteClick = async () => {
    showAlert(`Вы действительно хотите удалить данный протокол?`, 'sure', async () => {
      const response = await removeProtocol();
      if (!response.success)
        return alarmProtocolUsingInSchemeAlert(response.error_code, response.error);
      successProtocolDeletetAlert();
      resetProtocol();
    });
  };

  const handleSwitchChange = async () => {
    setIsActive(await toggleActivateProtocol());
  };

  const successProtocolUpsertAlert = () =>
    showAlert(
      selectedProtocol()?.id != null ? `Протокол успешно изменен!` : 'Протокол добавлен успешно!',
      'success',
    );

  const successProtocolDeletetAlert = () => showAlert(`Протокол удалён успешно!`, 'success');

  const warningProtocolWillChangeAlert = (callback) =>
    showAlert(`Данные протокола будут изменены!`, 'warning', callback);

  const alarmProtocolUsingInSchemeAlert = (error_code, error) =>
    showAlert(` Код № ${error_code}${error ? ` - ${error}` : ''}!`, 'error');

  const alarmNameNotUniqueAlert = () =>
    showAlert(`Протокол с таким именем уже существует в данной группе!`, 'error');

  const alarmAddDeviceInProtocolAlert = () =>
    showAlert(
      `Невозможно сохранить протокол, необходимо добавить как минимум 1 действие!`,
      'error',
    );

  const errorCommon = (code, message) =>
    showAlert(` Код № ${code}${message ? ` - ${message}` : ''}!`, 'error');

  const operationsMemo = useMemo(
    () =>
      operations?.map((item, index) => (
        <ProtocolOperation
          key={index}
          className={classNames(styles.operationBlock)}
          index={index}
          item={item}
          moveItem={moveOperationListItem}
        />
      )),
    [operations],
  );

  const ToolbarActions = () =>
    editMode ? (
      <>
        <button className={`button_default save_style`} onClick={() => handleSubmitClick()}>
          Сохранить
        </button>
        <button className={`button_default cancel_style`} onClick={() => handleCancelClick()}>
          Отмена
        </button>
      </>
    ) : (
      <>
        <div className={styles.wrapperActions}>
          <div className={styles.wrapperSwitch}>
            <SwitchWidget selected={isActive} onChange={handleSwitchChange} />
            <div className={styles.titleSwitch}>Вкл./Выкл.</div>
          </div>
          <button className={`button_default save_style`} onClick={() => handleEditClick()}>
            Редактировать
          </button>
          <div className={styles.deleteIcon} onClick={() => handleDeleteClick()}></div>
        </div>

        <div className={styles.titleProtocol}>{name}</div>
      </>
    );

  return (
    <div className={styles.pageContainer}>
      <div className={styles.toolbar}>
        <ToolbarActions />
      </div>
      <div className={styles.protocolContainer}>
        {editMode && (
          <div className={styles.header}>
            <div className={styles.formField}>
              <TextField
                label={'Наименование'}
                field={name}
                onChange={setName}
                required
                isFormValidate={isFormValidate}
              ></TextField>
            </div>
            <div className={styles.formField}>
              <div className={styles.formFieldContainer}>
                <div className={styles.label}>Группа</div>
                <DropdownComponent
                  items={groupsOptions}
                  keyItem={'id'}
                  valueItem={'name'}
                  selectedKey={selectedGroup}
                  onChange={(value) => setSelectedGroup(value)}
                />
              </div>
              {!selectedGroup && isFormValidate && (
                <div className={styles.dataErrorBlock}>
                  <img src={attentionIcon} alt={'attention'} />
                  <div className={styles.dataError}>Поле обязательно для заполнения</div>
                </div>
              )}
            </div>
          </div>
        )}
        {operationsMemo}
        {editMode && (
          <div className={classNames(styles.operationBlock, styles.addOperationBlock)}>
            <button
              className={styles.addOperationButton}
              onClick={() => {
                setOperations([
                  ...operations,
                  {
                    text: '',
                    time: {
                      value: '00:00',
                      setting: { date: '00:00' },
                    },
                    devices: [],
                  },
                ]);
              }}
            >
              <div className={classNames(styles.addIconContainer)}>
                <Icon
                  className={classNames(styles.addIcon)}
                  color={'#01b454'}
                  src={addIcon}
                  width={'14px'}
                  height={'14px'}
                />
              </div>
              <span>Добавить действие</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default UpsertProtocol;
